import React, { useRef, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Dimensions,
  Animated,
  Platform,
  InteractionManager,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { StatusBar } from "expo-status-bar";
import Header from "../components/Header";
import { LinearGradient } from "expo-linear-gradient";
import { useTheme } from "../contexts/ThemeContext";
import { 
  updateDocumentTitle, 
  updateMetaTag, 
  updateCanonicalLink, 
  generatePageTitle, 
  generateCanonicalUrl 
} from "../utils/seoUtils";

const { width } = Dimensions.get("window");
const isMobile = width < 768;
const isTabletOrDesktop = width >= 768;
const isSmallMobile = width < 380;

// Themed colors - matching HomeScreen patterns
const getThemedColors = (theme: 'light' | 'dark') => ({
  background: theme === "dark" ? "#18181B" : "#FAFAFA",
  card: theme === "dark" ? "#232327" : "#FFFFFF",
  text: theme === "dark" ? "#F2F2F2" : "#18181B",
  subText: theme === "dark" ? "#C2C2C2" : "#52525B",
  accent: theme === "dark" ? "#60A5FA" : "#3B82F6",
  border: theme === "dark" ? "#34343B" : "#E4E4E7",
  highlight: theme === "dark" ? "#1E293B" : "#F7FBFF",
  label: theme === "dark" ? "#9CA3AF" : "#718096",
  section: theme === "dark" ? "#1E1E23" : "#F7FAFC",
  ctaGradient: (theme === "dark" ? ["#60A5FA", "#8B5CF6"] : ["#3B82F6", "#6366F1"]) as readonly [string, string, ...string[]],
  ctaText: "rgba(255,255,255,0.95)",
  heroGradient: theme === "dark" ? ["#232327", "#1E1E23"] : ["#FFFFFF", "#F8FAFC"],
  iconColor: theme === "dark" ? "#60A5FA" : "#3B82F6",
  pillBg: theme === "dark" ? "rgba(96, 165, 250, 0.1)" : "rgba(59, 130, 246, 0.1)",
  pillText: theme === "dark" ? "#60A5FA" : "#3B82F6",
  quoteBg: theme === "dark" ? "rgba(96, 165, 250, 0.05)" : "rgba(59, 130, 246, 0.05)",
  quoteBorder: theme === "dark" ? "#60A5FA" : "#3B82F6",
});

interface PrivacyPolicyScreenProps {
  navigation: any;
}

const PrivacyPolicyScreen: React.FC<PrivacyPolicyScreenProps> = ({ navigation }) => {
  const { theme } = useTheme();
  const themed = getThemedColors(theme);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;

  // SEO setup for privacy policy page
  useEffect(() => {
    const title = generatePageTitle('Privacy Policy');
    const description = 'Learn about our privacy policy and how BC STEM Opportunities protects your personal information. We are committed to maintaining your privacy and data security.';
    
    updateDocumentTitle(title);
    updateMetaTag('description', description);
    updateCanonicalLink(generateCanonicalUrl('/privacy-policy'));
    
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', generateCanonicalUrl('/privacy-policy'), true);
  }, []);

  useEffect(() => {
    // Enhanced animations similar to HomeScreen
    InteractionManager.runAfterInteractions(() => {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 50,
          friction: 8,
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [fadeAnim, slideAnim, scaleAnim]);

  const renderSection = (title: string, content: string[], icon?: string, highlight = false) => (
    <Animated.View 
      style={[
        styles.section, 
        { 
          backgroundColor: highlight ? themed.quoteBg : themed.card,
          borderColor: highlight ? themed.quoteBorder : themed.border,
          borderWidth: highlight ? 2 : 1,
          transform: [{ scale: scaleAnim }],
        }
      ]}
    >
      <View style={styles.sectionHeader}>
        {icon && (
          <View style={[styles.iconContainer, { backgroundColor: themed.pillBg }]}>
            <Ionicons name={icon as any} size={20} color={themed.iconColor} />
          </View>
        )}
        <Text style={[styles.sectionTitle, { color: themed.text }]}>
          {title}
        </Text>
      </View>
      {content.map((paragraph, index) => (
        <Text
          key={index}
          style={[
            styles.sectionText,
            { 
              color: themed.subText, 
              marginBottom: index === content.length - 1 ? 0 : 16,
              lineHeight: isSmallMobile ? 22 : 26,
            }
          ]}
        >
          {paragraph}
        </Text>
      ))}
    </Animated.View>
  );

  const privacyContent = {
    introduction: [
      "Welcome to BC STEM Opportunities Database! We believe privacy is a fundamental right, and we've built our platform with that principle at its core.",
      "This policy explains our commitment to protecting your privacy and outlines our minimal data practices. Our goal is complete transparency about how we operate."
    ],
    dataCollection: [
      "🛡️ Zero Personal Data Collection: We fundamentally do not collect, store, or process any personal information about our users. No names, emails, addresses, or personal identifiers.",
      "🚫 No Tracking Technologies: We don't use cookies, tracking pixels, fingerprinting, or any surveillance technologies to monitor your browsing behavior.",
      "✨ No Account Required: You can explore all opportunities without creating an account or providing any personal information whatsoever.",
      "🔒 Privacy by Design: Our entire system is architected to function without needing to know who you are."
    ],
    dataUsage: [
      "Since we collect zero personal information, there's simply nothing personal to use, share, or sell.",
      "We don't build user profiles, track browsing habits, or create behavioral data sets.",
      "No targeted advertising, no data broker sales, no third-party data sharing - because we don't have any personal data to begin with.",
      "Your privacy isn't a setting you need to configure - it's built into our foundation."
    ],
    technicalData: [
      "To keep our service running smoothly, we may collect minimal, anonymous technical information:",
      "📱 Device compatibility data (screen size, operating system) to optimize display",
      "🌐 Browser type and version for compatibility assurance",
      "📊 Anonymous usage patterns (page views, popular searches) for performance optimization",
      "⚡ Error logs and performance metrics to maintain service reliability",
      "",
      "Important: This data is completely anonymous, aggregated, and cannot be traced back to any individual user."
    ],
    thirdPartyServices: [
      "We use carefully selected third-party services to deliver our platform:",
      "☁️ Vercel for secure, fast content delivery and hosting",
      "📈 Anonymous analytics services for performance monitoring (no personal data shared)",
      "🔧 Content delivery networks for optimal loading speeds",
      "",
      "All our third-party partners are contractually bound to respect user privacy and operate under strict no-personal-data policies."
    ],
    dataRetention: [
      "Since we don't collect personal information, there's no personal data to retain, store, or delete.",
      "Anonymous technical logs are automatically purged every 30 days to minimize any data footprint.",
      "We maintain only the minimum technical data necessary for security and performance, all fully anonymized."
    ],
    yourRights: [
      "🎯 Right to Anonymous Access: Use our entire platform without revealing your identity",
      "🚪 Right to Leave: Stop using our service anytime with zero data retention concerns",
      "❓ Right to Transparency: Ask us anything about our privacy practices",
      "🔍 Right to Verification: We're open about our technical implementation and data practices",
      "",
      "Your privacy isn't something you need to request - it's automatically protected by our design."
    ],
    changes: [
      "We may update this privacy policy to reflect service improvements or legal requirements.",
      "Any changes will always maintain our core principle: zero personal data collection.",
      "Updates will be posted here with clear change notifications and effective dates.",
      "We encourage periodic review, though our core privacy commitments will never weaken."
    ],
    contact: [
      "Questions about our privacy practices? We'd love to hear from you!",
      "We're committed to transparent communication and will respond promptly to any privacy-related inquiries.",
      "Our team is passionate about privacy and happy to discuss our technical implementation and privacy safeguards."
    ]
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: themed.background }]}>
      <StatusBar style={theme === "dark" ? "light" : "dark"} backgroundColor={themed.background} />
      <Header />
      
      <Animated.View 
        style={[
          styles.content, 
          { 
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }]
          }
        ]}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
        >
          {/* Hero Header Section */}
          <Animated.View style={[styles.headerSection, { transform: [{ scale: scaleAnim }] }]}>
            <LinearGradient
              colors={themed.ctaGradient}
              style={styles.headerGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <View style={styles.headerContent}>
                <View style={styles.heroIconContainer}>
                  <Ionicons name="shield-checkmark" size={48} color={themed.ctaText} />
                </View>
                <Text style={[styles.headerTitle, { color: themed.ctaText }]}>
                  Privacy Policy
                </Text>
                <Text style={[styles.headerSubtitle, { color: themed.ctaText }]}>
                  Transparency in how we protect your privacy
                </Text>
                <View style={[styles.trustBadge, { backgroundColor: "rgba(255,255,255,0.15)" }]}>
                  <Ionicons name="leaf" size={16} color={themed.ctaText} />
                  <Text style={[styles.trustBadgeText, { color: themed.ctaText }]}>
                    Zero Data Collection
                  </Text>
                </View>
              </View>
            </LinearGradient>
          </Animated.View>

          {/* Last Updated Badge */}
          <Animated.View 
            style={[
              styles.updateBadge, 
              { 
                backgroundColor: themed.pillBg,
                transform: [{ scale: scaleAnim }]
              }
            ]}
          >
            <Ionicons name="time" size={16} color={themed.pillText} />
            <Text style={[styles.lastUpdated, { color: themed.pillText }]}>
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Text>
          </Animated.View>

          {/* Content Sections with icons and enhanced styling */}
          {renderSection("Introduction", privacyContent.introduction, "information-circle", true)}
          {renderSection("Data Collection", privacyContent.dataCollection, "shield-checkmark")}
          {renderSection("How We Use Information", privacyContent.dataUsage, "construct")}
          {renderSection("Technical Information", privacyContent.technicalData, "hardware-chip")}
          {renderSection("Third-Party Services", privacyContent.thirdPartyServices, "link")}
          {renderSection("Data Retention", privacyContent.dataRetention, "archive")}
          {renderSection("Your Rights", privacyContent.yourRights, "person-circle", true)}
          {renderSection("Changes to This Policy", privacyContent.changes, "refresh-circle")}
          {renderSection("Contact Us", privacyContent.contact, "mail")}

          {/* Bottom spacer */}
          <View style={styles.bottomSpacer} />
        </ScrollView>
      </Animated.View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  headerSection: {
    marginBottom: isSmallMobile ? 20 : 32,
  },
  headerGradient: {
    paddingVertical: isTabletOrDesktop ? 80 : isSmallMobile ? 50 : 60,
    paddingHorizontal: isSmallMobile ? 20 : 40,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
  },
  headerContent: {
    alignItems: "center",
    maxWidth: 900,
    alignSelf: "center",
    width: "100%",
  },
  heroIconContainer: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 50,
    backgroundColor: "rgba(255,255,255,0.15)",
  },
  headerTitle: {
    fontSize: isTabletOrDesktop ? 48 : isSmallMobile ? 28 : 36,
    fontWeight: "800",
    textAlign: "center",
    marginBottom: 12,
    fontFamily: "Inter-ExtraBold",
    letterSpacing: -1,
  },
  headerSubtitle: {
    fontSize: isSmallMobile ? 16 : 20,
    textAlign: "center",
    opacity: 0.95,
    fontFamily: "Inter-Medium",
    marginBottom: 20,
    lineHeight: isSmallMobile ? 24 : 28,
  },
  trustBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8,
  },
  trustBadgeText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  updateBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
    marginBottom: 32,
    marginTop: -16,
    gap: 8,
  },
  section: {
    marginHorizontal: isSmallMobile ? 16 : 24,
    marginBottom: 20,
    padding: isSmallMobile ? 20 : 28,
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 6,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    gap: 12,
  },
  iconContainer: {
    padding: 12,
    borderRadius: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: isTabletOrDesktop ? 24 : isSmallMobile ? 18 : 20,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
    flex: 1,
    letterSpacing: -0.5,
  },
  sectionText: {
    fontSize: isSmallMobile ? 15 : 16,
    fontFamily: "Inter-Regular",
    letterSpacing: 0.1,
  },
  lastUpdated: {
    fontSize: 14,
    fontFamily: "Inter-Medium",
    fontWeight: "500",
  },
  bottomSpacer: {
    height: 60,
  },
});

export default PrivacyPolicyScreen; 